/* eslint-disable no-nested-ternary */
import React, {useEffect, useMemo, useState} from 'react'
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {SchemaOf} from 'yup'
import {makeStyles, Radio, RadioGroup} from '@material-ui/core'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import CardWithTitle from 'components/atoms/CardWithTitle'
import IndicatorProduct from 'pages/ProductsPages/ProductsDetail/Components/IndicatorProduct'
import ProductComposition from 'pages/ProductsPages/ProductsDetail/Components/ProductComposition'
import TotalPrice from 'pages/ProductsPages/ProductsDetail/Components/TotalPrice'
import useFailureModal from 'features/modal/useFailureModal'
import useProductManagement from 'features/productManagement/useProductManagement'
import AppLoading from 'components/atoms/Loading'
import useSuccessModal from 'features/modal/useSuccessModal'
import {
  AdminProductParamResponse,
  ChargeModeType,
  fetchProductDetailApi,
  ListPackageResponse,
  OfferItem,
  OfferItemsType,
  PricesInterface,
  ProductAdminDetailResponse,
  uploadGoodAttachmentApi,
  uploadGoodAttachmentFileToS3,
} from 'api/productApi'
import {getOrganizationType} from 'api/storageApi'
import {navigate} from 'AppHistory'
import {isOrganizationHealth} from 'Authority'
import RouteConstants, {getProductDetailPath} from 'constants/RouteConstant'
import {
  PBM_CARE,
  PAYMENT_METHOD,
  PaymentMethod,
  PREPAYMENT_ITEM,
  PRODUCT_DATA_INIT,
  TYPE_INDICATOR_PRODUCT,
  TYPE_INDICATOR_PRODUCT_OPTIONS_HEALTH,
  TypeIndicatorProduct,
  UNIT_PRICES,
  USAGE_PERIOD_UNIT,
  TARGET_ORG_TYPE,
  TARGET_TYPE,
  ORG_ID_APPOINT,
  DEFAULT_SALE_TARGET,
  ALL_SALE_TARGET,
  EEG_INDICATOR_ID,
  HRV_INDICATOR_ID,
  MCI_CLINIC_INDICATOR_ID,
  MCI_INDICATOR_ID,
  TARGET_NATION,
} from 'pages/ProductsPages/constant'
import ProductsDetailStyle from 'pages/ProductsPages/ProductsDetail/Style'
import {CurrencyCode} from 'api/paymentApi'
import {centToUSD, usdToCent} from 'helpers/paymentHelper'

const useStyles = makeStyles(ProductsDetailStyle)
export type ProductSchema = {
  nameEn: string
  nameKo: string
  offerItems: ListPackageResponse[]
  totalPriceKo: number
  totalPriceEn: number
}

const ProductsDetail = () => {
  const {t} = useTranslation()
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()
  const {
    loading,
    listAllPackage,
    getPackageList,
    onCreate,
    onUpdate,
    onUpdateDisplayInfo,
    getIndicatorType,
    isEqualSaleTarget,
  } = useProductManagement()
  const {id: productId} = useParams<{id: string}>()
  const organizationType = getOrganizationType()
  const isHealthOrg = isOrganizationHealth(organizationType)
  const UpdatePatientSchema: SchemaOf<ProductSchema> = yup
    .object()
    .shape({
      nameEn: yup
        .string()
        .required(t('INameRequired'))
        .max(255, t('IMaxName', {max: 255})),
      nameKo: yup
        .string()
        .required(t('INameRequired'))
        .max(255, t('IMaxName', {max: 255})),
      offerItems: yup
        .array()
        .of(yup.object())
        .required('ISelectAtLeastOneItem'),
      totalPriceKo: yup
        .number()
        .moreThan(0, {message: t('IPriceMustBeGreaterThanZero')}),
      totalPriceEn: yup
        .number()
        .moreThan(0, {message: t('IPriceMustBeGreaterThanZero')}),
    })
    .defined()

  const {
    clearErrors,
    formState: {errors},
    setValue,
    setError,
  } = useForm<ProductSchema>({resolver: yupResolver(UpdatePatientSchema)})

  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()

  const [typeIndicator, setTypeIndicator] =
    useState<TypeIndicatorProduct | null>(null)
  const [offerType, setOfferType] = useState<string>(
    PREPAYMENT_ITEM.MONTHLY_OFFER,
  )
  const [offerUnit, setOfferUnit] = useState<string>(PBM_CARE.MINUTES)
  const [totalKRW, setTotalKRW] = useState<number>(0)
  const [totalUSD, setTotalUSD] = useState<number>(0)
  const [unlimitPackage, setUnlimitPackage] = useState<boolean>(false)
  const [productData, setProductData] =
    useState<AdminProductParamResponse>(PRODUCT_DATA_INIT)
  const [listPackageItem, setListPackageItem] = useState<
    ListPackageResponse[] | null
  >(null)
  // State save data detail
  const [typeIndicatorDetail, setTypeIndicatorDetail] =
    useState<TypeIndicatorProduct | null>(null)
  const [dataDetail, setDataAPIDetail] =
    useState<ProductAdminDetailResponse | null>(null)
  const [isAllRegion, setIsAllRegion] = useState<boolean>(false)
  const [attachmentGood, setAttachmentGood] = useState<{
    fileName: string
    fileKey: string
    eTag: string
  }>({
    fileName: '',
    fileKey: '',
    eTag: '',
  })
  const [oldAttachmentName, setOldAttachmentName] = useState('')
  const [isShowOldAttachment, setIsShowOldAttachment] = useState(false)

  const isCreate = useMemo(
    () => location.pathname === RouteConstants.PRODUCTS_MGS_CREATE.path,
    [location],
  )

  const isUpdate = Boolean(
    useRouteMatch(RouteConstants.PRODUCTS_MGS_UPDATE.path),
  )

  const isDetail = Boolean(
    useRouteMatch(RouteConstants.PRODUCTS_MGS_DETAIL.path),
  )

  const disableUpdating = useMemo(() => {
    return isDetail
  }, [isDetail])

  const submitLabelKey = useMemo(() => {
    let titleKey = 'ISave'

    if (isCreate) {
      titleKey = 'IProductCreate'
    } else if (isDetail) {
      titleKey = 'IEditProduct'
    }
    return titleKey
  }, [isCreate, isUpdate])

  const changeIndicatorSelectionLogic = (list: ListPackageResponse[]) => {
    const listAdd: string[] = []
    const listRemove: string[] = []

    const indicatorRules = [
      {
        indicatorId: EEG_INDICATOR_ID,
        relatedClasses: ['EEG', 'EDF', 'EEG_HRV'],
      },
      {
        indicatorId: HRV_INDICATOR_ID,
        relatedClasses: ['HRV', 'EEG_HRV'],
      },
      {
        indicatorId: MCI_INDICATOR_ID,
        triggerId: MCI_CLINIC_INDICATOR_ID,
      },
    ]

    indicatorRules.forEach((rule) => {
      const {indicatorId, relatedClasses, triggerId} = rule

      if (relatedClasses) {
        const hasRelatedSelected = list.some(
          (item) => relatedClasses.includes(item.cls) && item.select,
        )

        const isIndicatorSelected = list.some(
          (item) => item.itemId === indicatorId && item.select,
        )

        if (hasRelatedSelected) {
          listAdd.push(indicatorId)
        } else if (isIndicatorSelected) {
          listRemove.push(indicatorId)
        }
      }

      if (triggerId) {
        const triggerSelected = list.some(
          (item) => item.itemId === triggerId && item.select,
        )

        if (triggerSelected) {
          listAdd.push(indicatorId)
        }
      }
    })

    return list.map((item) => {
      if (listAdd.includes(item.itemId.toString()))
        return {...item, select: true}
      if (listRemove.includes(item.itemId.toString()))
        return {...item, select: false}
      return item
    })
  }

  const handleChooseType = (id: number | string) => {
    if (listPackageItem?.length) {
      const updatedList = listPackageItem.map((item) => {
        if (item.itemId === id) {
          return {...item, select: !item.select}
        }
        return item
      })
      const tempList = changeIndicatorSelectionLogic(updatedList)
      setListPackageItem(tempList)

      if (tempList.find((item) => item.select)) {
        clearErrors('offerItems')
      }
    }
  }

  const getPrices = (data: PricesInterface[], key: string) => {
    let value = 0
    data.forEach((item) => {
      if (item.currency === key) {
        value = item.price
      }
    })
    return value
  }

  const setOfferAmount = (id: string, value: string) => {
    if (!listPackageItem) return

    const updatedList = listPackageItem.map((item) =>
      item.itemId === id ? {...item, offerAmount: Number(value)} : item,
    )

    setListPackageItem(updatedList)
  }

  const setUnitPrices = (id: string, value: string, key: string) => {
    if (!listPackageItem) return
    const updated = listPackageItem.map((item) => {
      if (item.itemId === id) {
        const newUnitPrices = item.unitPrices.map((priceObj) => {
          if (priceObj.currency === key) {
            return {
              ...priceObj,
              price: Number(value),
            }
          }
          return priceObj
        })

        return {
          ...item,
          unitPrices: newUnitPrices,
        }
      }
      return item
    })

    setListPackageItem(updated)
  }

  const handleCountAmount = () => {
    const totalWon =
      productData.sale.discountRate > 0
        ? totalKRW - (totalKRW * productData.sale.discountRate) / 100
        : totalKRW

    const totalDollar =
      productData.sale.discountRate > 0
        ? totalUSD - (totalUSD * productData.sale.discountRate) / 100
        : totalUSD
    productData.sale.salePrices.forEach((item) => {
      if (item.currency === UNIT_PRICES.KRW) {
        item.price = totalWon
      }
      if (item.currency === UNIT_PRICES.USD) {
        item.price = totalDollar
      }
    })
    setProductData({...productData})
  }

  const setPaymentMethod = (value: PaymentMethod) => {
    productData.sale.paymentMethod = value
    if (value === PAYMENT_METHOD.MANUAL) {
      setOfferType(PREPAYMENT_ITEM.PROVIDER)
    }
    setProductData({...productData})
  }
  const setNameProduct = (value: string, key: string) => {
    productData.goods.displayInfos.forEach((item) => {
      if (item.langCode === key) {
        item.name = value
      }
    })
    setProductData({...productData})
    if (value) {
      if (key === 'en') {
        clearErrors('nameEn')
        setValue('nameEn', value)
      }
      if (key === 'ko') {
        clearErrors('nameKo')
        setValue('nameKo', value)
      }
    } else {
      setError(key === 'en' ? 'nameEn' : 'nameKo', {
        type: 'manual',
        message: t('INameRequired'),
      })
    }
  }

  const setTargetNation = (
    value: string,
    key: string,
    resetOrgType?: boolean,
  ) => {
    if (!productData.sale.saleTarget) {
      productData.sale.saleTarget = DEFAULT_SALE_TARGET
    }
    const listKeyTarget = productData.sale.saleTarget.targets.map(
      (item) => item.targetType,
    )
    if (resetOrgType) {
      productData.sale.saleTarget.targets =
        productData.sale.saleTarget.targets.filter(
          (item) => item.targetType !== TARGET_TYPE.ORG_TYPE,
        )
      setProductData({...productData})
      return
    }
    if (key === TARGET_TYPE.ORG_TYPE) {
      if (listKeyTarget.indexOf(TARGET_TYPE.ORG_TYPE) === -1) {
        productData.sale.saleTarget.targets.push({
          targetType: key,
          targetId: value,
        })
        setProductData({...productData})
        return
      }
      const tempTarget = productData.sale.saleTarget.targets.map((item) => {
        if (item.targetType === key) {
          return {
            ...item,
            targetId: value,
          }
        }
        return item
      })
      productData.sale.saleTarget.targets = tempTarget
      setProductData({...productData})
      return
    }
    if (
      key === TARGET_TYPE.REGION &&
      listKeyTarget.indexOf(TARGET_TYPE.REGION) === -1
    ) {
      productData.sale.saleTarget.targets.push({
        targetType: key,
        targetId: value,
      })
      setProductData({...productData})
      return
    }

    if (key === TARGET_TYPE.REGION) {
      if (value === 'all') setIsAllRegion(true)
      else setIsAllRegion(false)
    }

    if (key === TARGET_TYPE.ORG_ID && value === ORG_ID_APPOINT) {
      if (listKeyTarget.indexOf(TARGET_TYPE.ORG_ID) === -1) {
        productData.sale.saleTarget.targets.push({
          targetType: key,
          targetId: value,
        })
        setProductData({...productData})
        return
      }
      if (listKeyTarget.indexOf(TARGET_TYPE.ORG_ID) !== -1) {
        productData.sale.saleTarget.targets =
          productData.sale.saleTarget.targets.filter(
            (item) => item.targetType !== key,
          )
        setProductData({...productData})
        return
      }
    }

    if (key === 'Region' && productData.sale.saleTarget.targets)
      productData.sale.saleTarget.targets.forEach((item) => {
        if (item.targetType === key) {
          item.targetId = value
        }
      })
    setProductData({...productData})
  }

  const setPrepaymentSelect = (value: string) => {
    setOfferType(value)
  }

  const setPrices = (value: string, key: string) => {
    const newPrices = productData.sale.prices.map((price) => {
      if (price.currency === key) {
        return {
          ...price,
          price: Number(value),
        }
      }
      return price
    })
    setProductData({
      ...productData,
      sale: {...productData.sale, prices: newPrices},
    })
    if (key === UNIT_PRICES.KRW) {
      setTotalKRW(Number(value))
    }
    if (key === UNIT_PRICES.USD) {
      setTotalUSD(Number(value))
    }
  }

  const setSalePrices = (value: string, key: string) => {
    const newSalesPrices = productData.sale.salePrices.map((price) => {
      if (price.currency === key) {
        return {
          ...price,
          price: Number(value),
        }
      }
      return price
    })
    setProductData({
      ...productData,
      sale: {...productData.sale, salePrices: newSalesPrices},
    })
  }

  const setDiscountRate = (value: number) => {
    const data = {...productData}
    data.sale.discountRate = value
    setProductData({...data})
  }

  const setDescription = (value: string, key: string) => {
    productData.goods.displayInfos.forEach((item) => {
      if (item.langCode === key) {
        item.description = value
      }
    })
    setProductData({...productData})
  }

  const setPeriodProduct = (value: string) => {
    productData.offer.usagePeriod = Number(value)
    setProductData({...productData})
  }

  const setChargeMode = (value: ChargeModeType) => {
    productData.sale.chargeMode = value
    setProductData({...productData})
  }

  const setChargeQuantity = (value: number) => {
    productData.sale.chargeQuantity = value
    setProductData({...productData})
  }

  const changeUnlimitPeriod = (value: boolean) => {
    const offer = {
      ...productData.offer,
      usagePeriodUnit: USAGE_PERIOD_UNIT.DAY,
      usagePeriod: value ? 120 : productData.offer.usagePeriod,
    }
    setUnlimitPackage(value)
    setProductData({...productData, offer})
  }

  const handleBackToList = () => {
    if (isUpdate) {
      setAttachmentGood({
        fileName: oldAttachmentName,
        fileKey: '',
        eTag: '',
      })
      setIsShowOldAttachment(false)
      history.push(getProductDetailPath(productId))
    } else navigate(RouteConstants.PRODUCTS_MGS_LIST.path)
  }
  const resetPayload = () => {
    const updatedProductData = {
      ...productData,
      goods: {
        ...productData.goods,
        displayInfos: productData.goods.displayInfos.map((item) => ({
          ...item,
          name: '',
          description: '',
          imageUrl: '',
        })),
      },
    }
    setOldAttachmentName('')

    setProductData(updatedProductData)
  }

  const checkValidate = (data: AdminProductParamResponse) => {
    let passValidate = true
    if (!listPackageItem?.find((item) => item.select)) {
      setError('offerItems', {
        type: 'manual',
        message: t('ISelectAtLeastOneItem'),
      })
      passValidate = false
    }
    if (
      listPackageItem?.some(
        (item) =>
          item.select &&
          item.unitPrices.find((price) => price.currency === UNIT_PRICES.KRW)
            ?.price === 0,
      ) &&
      productData.sale.paymentMethod !== PAYMENT_METHOD.MANUAL
    ) {
      setError('offerItems', {
        type: 'manual',
        message: t('ISetPriceForSelectedItems', {
          unitPrice: CurrencyCode.KRW,
        }),
      })
      passValidate = false
    } else if (
      listPackageItem?.some(
        (item) =>
          item.select &&
          item.unitPrices.find((price) => price.currency === UNIT_PRICES.USD)
            ?.price === 0,
      ) &&
      productData.sale.paymentMethod !== PAYMENT_METHOD.MANUAL
    ) {
      setError('offerItems', {
        type: 'manual',
        message: t('ISetPriceForSelectedItems', {
          unitPrice: CurrencyCode.USD,
        }),
      })
      passValidate = false
    }
    const nationTarget = data.sale.saleTarget?.targets.find(
      (target) => target.targetType === 'Region',
    )?.targetId
    const nameEn = data.goods.displayInfos.find(
      (item) => item.langCode === 'en',
    )?.name
    if (
      (!nameEn || nameEn.trim() === '') &&
      nationTarget !== TARGET_NATION.DOMESTIC
    ) {
      setError('nameEn', {
        type: 'manual',
        message: t('INameRequired'),
      })
      passValidate = false
    }
    const nameKo = data.goods.displayInfos.find(
      (item) => item.langCode === 'ko',
    )?.name
    if (
      (!nameKo || nameKo.trim() === '') &&
      nationTarget !== TARGET_NATION.OVERSEAS
    ) {
      setError('nameKo', {
        type: 'manual',
        message: t('INameRequired'),
      })
      passValidate = false
    }
    const totalPriceKo =
      data.sale.salePrices.find((price) => price.currency === UNIT_PRICES.KRW)
        ?.price || 0
    const totalPriceEn =
      data.sale.salePrices.find((price) => price.currency === UNIT_PRICES.USD)
        ?.price || 0
    if (
      totalPriceKo <= 0 &&
      productData.sale.paymentMethod !== PAYMENT_METHOD.MANUAL
    ) {
      setError('totalPriceKo', {
        type: 'manual',
        message: t('IPriceMustBeGreaterThanZero'),
      })
      passValidate = false
    }
    if (
      totalPriceEn <= 0 &&
      productData.sale.paymentMethod !== PAYMENT_METHOD.MANUAL
    ) {
      setError('totalPriceEn', {
        type: 'manual',
        message: t('IPriceMustBeGreaterThanZero'),
      })
      passValidate = false
    }
    return passValidate
  }

  const mappingListPackage = (
    list: ListPackageResponse[],
    dataAPI: OfferItem[],
  ) => {
    const listReduce = [...list]
      .toSorted(
        (a, b) =>
          a.cls.localeCompare(b.cls) ||
          a.itemId.toString().localeCompare(b.itemId.toString()),
      )
      .map((item) => {
        return {
          ...item,
          offerAmount: 0,
        }
      })

    return listReduce.map((item) => {
      const dataAPIItem = dataAPI.find((dta) => dta.pkgItemId === item.itemId)
      const {unitPrices, offerAmount, offerUnit} = dataAPIItem || {}

      if (item.cls === 'PBM' && offerUnit) {
        setOfferUnit(offerUnit)
      }

      return {
        ...item,
        unitPrices: unitPrices ?? item.unitPrices,
        select: Boolean(
          unitPrices?.every(
            (price) => price.price > 0 || (offerAmount && offerAmount > 0),
          ),
        ),
        offerAmount: offerAmount ?? 0,
        type: offerUnit ?? item.type,
      }
    })
  }

  const fetchData = async () => {
    if (productId && !isCreate && listAllPackage?.length) {
      const dataAPI = await fetchProductDetailApi({goodsId: productId})
      if (dataAPI.data) {
        // setDataAPIDetail(dataAPI.data)
        setDataAPIDetail({
          ...dataAPI.data,
          offer: {
            ...dataAPI.data.offer,
            offerItems: dataAPI.data.offer.offerItems.map((item) => {
              return {
                ...item,
                unitPrices: item.unitPrices.map((price) => {
                  return {
                    ...price,
                    price:
                      price.currency === UNIT_PRICES.USD
                        ? usdToCent(price.price)
                        : price.price,
                  }
                }),
              }
            }),
          },
          sale: {
            ...dataAPI.data.sale,
            prices: dataAPI.data.sale.prices.map((price) => {
              return {
                ...price,
                price:
                  price.currency === UNIT_PRICES.USD
                    ? usdToCent(price.price)
                    : price.price,
              }
            }),
            salePrices: dataAPI.data.sale.salePrices.map((price) => {
              return {
                ...price,
                price:
                  price.currency === UNIT_PRICES.USD
                    ? usdToCent(price.price)
                    : price.price,
              }
            }),
          },
        })
        const indicatorType = getIndicatorType(dataAPI.data.sale.saleTarget)
        setTypeIndicator(indicatorType)
        setTypeIndicatorDetail(indicatorType)
        const packageItems = await getPackageList({target: indicatorType})
        const offerItemsAPI = mappingListPackage(
          packageItems,
          dataAPI.data.offer.offerItems.map((item) => {
            return {
              ...item,
              unitPrices: item.unitPrices.map((price) => {
                return {
                  ...price,
                  price:
                    price.currency === UNIT_PRICES.USD
                      ? usdToCent(price.price)
                      : price.price,
                }
              }),
            }
          }),
          dataAPI.data.sale.paymentMethod,
        )
        if (
          !dataAPI.data.sale.saleTarget?.targets.find(
            (target) => target.targetType === TARGET_TYPE.REGION,
          ) ||
          dataAPI.data.sale.saleTarget?.targets.filter(
            (target) => target.targetType === TARGET_TYPE.REGION,
          ).length > 1
        ) {
          setIsAllRegion(true)
        }
        setListPackageItem(offerItemsAPI)
        setUnlimitPackage(dataAPI.data.offer.usagePeriod === 120)
        setOfferType(
          dataAPI.data.offer.offerType || PREPAYMENT_ITEM.MONTHLY_OFFER,
        )
        setValue(
          'offerItems',
          offerItemsAPI.filter((item) => item.select),
        )
        setValue(
          'nameEn',
          dataAPI.data.goods.displayInfos.find((item) => item.langCode === 'en')
            ?.name ?? '',
        )
        setValue(
          'nameKo',
          dataAPI.data.goods.displayInfos.find((item) => item.langCode === 'ko')
            ?.name ?? '',
        )
        if (isEqualSaleTarget(dataAPI.data.sale.saleTarget, ALL_SALE_TARGET)) {
          dataAPI.data.sale.saleTarget = DEFAULT_SALE_TARGET
        }
        setProductData({
          ...dataAPI.data,
          offer: {
            ...dataAPI.data.offer,
            offerItems: dataAPI.data.offer.offerItems.map((item) => {
              return {
                ...item,
                unitPrices: item.unitPrices.map((price) => {
                  return {
                    ...price,
                    price:
                      price.currency === UNIT_PRICES.USD
                        ? usdToCent(price.price)
                        : price.price,
                  }
                }),
              }
            }),
          },
          sale: {
            ...dataAPI.data.sale,
            prices: dataAPI.data.sale.prices.map((price) => {
              return {
                ...price,
                price:
                  price.currency === UNIT_PRICES.USD
                    ? usdToCent(price.price)
                    : price.price,
              }
            }),
            salePrices: dataAPI.data.sale.salePrices.map((price) => {
              return {
                ...price,
                price:
                  price.currency === UNIT_PRICES.USD
                    ? usdToCent(price.price)
                    : price.price,
              }
            }),
          },
        })
        if (dataAPI.data.goods.attachment) {
          setAttachmentGood({
            fileName: decodeURIComponent(dataAPI.data.goods.attachment.name),
            fileKey: '',
            eTag: '',
          })
        }
      }
    }
    if (!productId) {
      setTypeIndicator(TYPE_INDICATOR_PRODUCT.HOSPITAL)
      const res = await getPackageList({
        target: TYPE_INDICATOR_PRODUCT.HOSPITAL,
      })
      const tempRes = res
        .map((item) => ({
          ...item,
          select: false,
        }))
        .toSorted(
          (a, b) =>
            a.cls.localeCompare(b.cls) ||
            a.itemId.toString().localeCompare(b.itemId.toString()),
        )
      setListPackageItem(tempRes)
    }
  }

  const handleFileUploading = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (file) {
      const payload = {
        fileName: file.name,
        contentType: file.type,
      }
      const res = await uploadGoodAttachmentApi(payload)
      const uploadRes = await uploadGoodAttachmentFileToS3({
        path: res.data.uploadUrl,
        header: res.data.headers,
        data: file,
      })
      setAttachmentGood({
        fileName: file.name,
        fileKey: res.data.fileKey,
        eTag: uploadRes.headers.etag,
      })
    }
  }

  const handleProductAction = async () => {
    const payload = {
      ...productData,
    }

    const regionTargetId = payload.sale.saleTarget?.targets.find(
      (target) => target.targetType === 'Region',
    )?.targetId

    const langToMask =
      regionTargetId === TARGET_NATION.DOMESTIC
        ? 'en'
        : regionTargetId === TARGET_NATION.OVERSEAS
        ? 'ko'
        : null

    if (langToMask) {
      payload.goods.displayInfos = payload.goods.displayInfos.map((info) =>
        info.langCode === langToMask
          ? {...info, name: 'NA', description: 'NA'}
          : info,
      )
    }
    if (!payload.sale.saleTarget) {
      payload.sale.saleTarget = DEFAULT_SALE_TARGET
    }

    const isValidate = checkValidate(productData)

    if (payload.offer.usagePeriod === 0) {
      onFailureModalOpen(t('IExpirationDateAtLeastOneMonth'))
      return
    }
    if (!isValidate) {
      console.log('❌ Validation failed')
      return
    }

    payload.offer.offerItems = listPackageItem
      ?.filter((item) => item.select)
      .map((item) => {
        return {
          offerUnit: item.cls !== 'PBM' ? PBM_CARE.CASE : offerUnit,
          offerAmount: item.offerAmount ?? 0,
          pkgItemId: item.itemId,
          unitPrices: item.unitPrices.map((price) =>
            price.currency === UNIT_PRICES.USD
              ? {...price, price: centToUSD(price.price)}
              : price,
          ),
        }
      }) as OfferItemsType[]

    payload.offer.offerType = offerType
    payload.sale.saleTarget.targets = payload.sale.saleTarget.targets.filter(
      (item) =>
        !(item.targetType === TARGET_TYPE.REGION && item.targetId === 'all'),
    )
    payload.sale = {
      ...payload.sale,
      prices: payload.sale.prices.map((price) =>
        price.currency === UNIT_PRICES.USD
          ? {...price, price: centToUSD(price.price)}
          : price,
      ),
      salePrices: payload.sale.salePrices.map((price) =>
        price.currency === UNIT_PRICES.USD
          ? {...price, price: centToUSD(price.price)}
          : price,
      ),
    }
    payload.goods = {
      ...payload.goods,
      ...(attachmentGood.fileKey && attachmentGood.eTag
        ? {
            attachment: {
              fileKey: attachmentGood.fileKey,
              checksum: attachmentGood.eTag,
            },
          }
        : {
            attachment: null,
            removalAttachment: !isShowOldAttachment,
          }),
    }

    let result = null
    try {
      if (productId && !isCreate) {
        result = await onUpdate({
          data: payload,
          goodsId: productId,
        })
      } else {
        result = await onCreate(payload)
      }

      if (result) {
        setIsShowOldAttachment(false)
        onSuccessModalOpen(t('IProcessSuccess'))
        resetPayload()
        if (productId && !isCreate) {
          navigate(
            RouteConstants.PRODUCTS_MGS_DETAIL.path.replace(
              ':id',
              result.id ?? productId,
            ),
          )
          fetchData()
        } else {
          navigate(RouteConstants.PRODUCTS_MGS_LIST.path)
        }
      } else {
        onFailureModalOpen(t('IProcessFail'))
      }
    } catch (error) {
      onFailureModalOpen(t('IProcessFail'))
    }
  }

  const disabledAccordingPaymentMethod = useMemo(() => {
    if (isCreate) return false
    if (
      productData.sale.paymentMethod === PAYMENT_METHOD.MANUAL ||
      productData.sale.paymentMethod === PAYMENT_METHOD.POST_SETTLEMENT
    ) {
      return true
    }
    return false
  }, [productData])

  const getTotalPrices = (data: ListPackageResponse[]) => {
    const {totalWon, totalDollar} = data.reduce(
      (acc, item) => {
        acc.totalWon +=
          getPrices(item.unitPrices, UNIT_PRICES.KRW) * (item.offerAmount || 0)
        acc.totalDollar +=
          getPrices(item.unitPrices, UNIT_PRICES.USD) * (item.offerAmount || 0)
        return acc
      },
      {totalWon: 0, totalDollar: 0},
    )
    return {
      totalWon,
      totalDollar,
    }
  }

  const getSaleObjectCreate = (totalWon: number, totalDollar: number) => {
    return {
      ...productData.sale,
      prices: [
        {
          currency: UNIT_PRICES.KRW,
          price: totalWon,
        },
        {
          currency: UNIT_PRICES.USD,
          price: totalDollar,
        },
      ],
      salePrices: [
        {
          currency: UNIT_PRICES.KRW,
          price:
            productData.sale.discountRate > 0
              ? totalWon - (productData.sale.discountRate * totalWon) / 100
              : totalWon,
        },
        {
          currency: UNIT_PRICES.USD,
          price:
            productData.sale.discountRate > 0
              ? totalDollar -
                (productData.sale.discountRate * totalDollar) / 100
              : totalDollar,
        },
      ],
    }
  }

  const handleNavigateUpdate = () => {
    if (attachmentGood.fileName) {
      setIsShowOldAttachment(true)
      setOldAttachmentName(attachmentGood.fileName)
      setAttachmentGood({
        fileName: '',
        fileKey: '',
        eTag: '',
      })
    }
    navigate(RouteConstants.PRODUCTS_MGS_UPDATE.path.replace(':id', productId))
  }

  const renderNewPackageList = (list: ListPackageResponse[]) =>
    [...list]
      .toSorted(
        (a, b) =>
          a.cls.localeCompare(b.cls) ||
          a.itemId.toString().localeCompare(b.itemId.toString()),
      )
      .map((item) => {
        return {
          ...item,
          select: false,
          offerAmount: 0,
        }
      })

  const onChangeTypeIndicator = async (item: {
    key: TypeIndicatorProduct
    label: string
  }) => {
    const newListPackage = await getPackageList({target: item.key})
    setTypeIndicator(item.key)
    const listPackage =
      typeIndicatorDetail === item.key && dataDetail
        ? mappingListPackage(newListPackage, dataDetail.offer.offerItems)
        : renderNewPackageList(newListPackage)
    const productForOrg = productData.sale.saleTarget?.targets.find(
      (item) =>
        item.targetType === TARGET_TYPE.ORG_ID &&
        item.targetId === ORG_ID_APPOINT,
    )
    if (!productForOrg) {
      let saleTargetOrgType
      if (item.key === TYPE_INDICATOR_PRODUCT.HEALTH) {
        saleTargetOrgType = TARGET_ORG_TYPE.HEALTH
      } else if (item.key === TYPE_INDICATOR_PRODUCT.HOSPITAL) {
        saleTargetOrgType = TARGET_ORG_TYPE.HOSPITAL
      }
      if (saleTargetOrgType) {
        setTargetNation(saleTargetOrgType, TARGET_TYPE.ORG_TYPE)
      } else {
        setTargetNation('', TARGET_TYPE.ORG_TYPE, true)
      }
    }

    setListPackageItem(listPackage)
  }

  useEffect(() => {
    if (listPackageItem?.length) {
      const listProductChecked = listPackageItem.filter((item) => item.select)
      const {totalWon, totalDollar} = getTotalPrices(listProductChecked)
      setTotalKRW(totalWon)
      setTotalUSD(totalDollar)
      setProductData({
        ...productData,
        sale: getSaleObjectCreate(totalWon, totalDollar),
      })
    }
  }, [listPackageItem])

  useEffect(() => {
    resetPayload()
    fetchData()
  }, [productId, isCreate, listAllPackage])

  return (
    <div style={{minWidth: 1500}}>
      <AppLoading loading={loading || !listAllPackage?.length} />
      <CardWithTitle
        title={isCreate ? t('IAddProduct') : t('IProductsManagement')}
      >
        <h1 className={classes.titleCard} style={{fontSize: '18px'}}>
          ■ {t('ICreateAnalysisIndicators')}
        </h1>
        <div className={classes.paymentMethods}>
          <h3>{t('IIndicatorProductType')} :</h3>
          <RadioGroup className={classes.groupRadio}>
            {TYPE_INDICATOR_PRODUCT_OPTIONS_HEALTH.map((item) => {
              return (
                <label
                  key={item.key}
                  htmlFor={item.key}
                  className={`${classes.fieldRadio} ${classes.monthlyInstallmentLabel}`}
                >
                  <Radio
                    color='primary'
                    className={classes.p0}
                    id={item.key}
                    name={item.key}
                    value={item.key}
                    checked={typeIndicator === item.key}
                    onChange={() => {
                      onChangeTypeIndicator(item)
                    }}
                    disabled={disableUpdating}
                  />
                  {t(item.label)}
                </label>
              )
            })}
          </RadioGroup>
        </div>

        <IndicatorProduct
          disabledAll={disableUpdating}
          typeIndicator={typeIndicator || ''}
          listPackage={listPackageItem || []}
          disabledAccordingPaymentMethod={disabledAccordingPaymentMethod}
          errors={errors}
          handleChooseType={handleChooseType}
          setUnitPrices={setUnitPrices}
          setOfferAmount={setOfferAmount}
          disabledByManualPayment={
            productData.sale.paymentMethod === PAYMENT_METHOD.MANUAL
          }
        />

        <h1 className={classes.H1} style={{fontSize: '18px'}}>
          ■ {t('IProductComposition')}
        </h1>

        <ProductComposition
          unlimitPackage={unlimitPackage}
          disabledAll={disableUpdating}
          data={productData}
          offerType={offerType}
          setPaymentMethod={setPaymentMethod}
          setTargetNation={setTargetNation}
          setPrepaymentSelect={setPrepaymentSelect}
          setPeriodProduct={setPeriodProduct}
          changeUnlimitPeriod={changeUnlimitPeriod}
          setChargeMode={setChargeMode}
          setChargeQuantity={setChargeQuantity}
        />

        <TotalPrice
          disabledAll={disableUpdating}
          data={productData}
          disabledAccordingPaymentMethod={disabledAccordingPaymentMethod}
          errors={errors}
          setPrices={setPrices}
          setNameProduct={setNameProduct}
          setSalePrices={setSalePrices}
          setDescription={setDescription}
          setDiscountRate={setDiscountRate}
          countAmount={handleCountAmount}
          isAllRegion={isAllRegion}
          attachmentGood={attachmentGood}
          onUploadingFile={handleFileUploading}
          isShowAttachmentUploadBtn={isUpdate || isCreate}
          isShowOldAttachment={isShowOldAttachment}
          oldAttachmentName={oldAttachmentName}
          onRemoveAttachment={() => setIsShowOldAttachment(false)}
        />
        <div className={classes.listBBtn}>
          <ActionButton onClick={handleBackToList} color='primary'>
            {isUpdate ? t('ICancel') : t('IInventory')}
          </ActionButton>
          <ActionButton
            onClick={
              isCreate || isUpdate ? handleProductAction : handleNavigateUpdate
            }
            color='primary'
          >
            {t(submitLabelKey)}
          </ActionButton>
        </div>
      </CardWithTitle>
    </div>
  )
}

export default ProductsDetail
